import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, ArrowUpRight, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmt, ddmmyyyy } from "@/lib/format";
import { AppHeader } from "@/components/AppHeader";
import { BookDialog } from "@/components/BookDialog";
import { BooksDashboard } from "@/components/BooksDashboard";
import { useAuth } from "@/context/AuthContext";

export default function BooksPage() {
  const [books, setBooks] = useState(null);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const { can } = useAuth();

  const load = () => api.get("/books").then((r) => setBooks(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen grid-paper">
      <AppHeader />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <div className="flex items-end justify-between gap-4 mb-10 fade-up">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2">Your ledgers</p>
            <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight">Books</h1>
          </div>
          {can("books") && <button data-testid="create-book-btn" onClick={() => setOpen(true)}
            className="h-11 px-4 bg-slate-900 text-white font-semibold flex items-center gap-2 hover:bg-slate-800 active:scale-[0.98] transition-[background-color,transform]">
            <Plus size={16} /> New book
          </button>}
        </div>

        {books === null && <p className="text-sm text-slate-500" data-testid="books-loading">Loading books…</p>}
        {books?.length === 0 && (
          <div data-testid="books-empty" className="border border-dashed border-slate-400 bg-white/60 p-10 text-center">
            <p className="font-semibold">No books yet</p>
            <p className="text-sm text-slate-500 mt-1">{can("books") ? "Create your first book to start entering daily balances." : "No books have been assigned to you yet. Ask the admin."}</p>
          </div>
        )}
        {books?.length > 0 && <BooksDashboard books={books} />}
        <div className="grid sm:grid-cols-2 gap-4">
          {books?.map((b, i) => (
            <button key={b.id} data-testid={`book-card-${b.id}`} onClick={() => nav(`/book/${b.id}`)}
              style={{ animationDelay: `${i * 60}ms` }}
              className="fade-up group text-left bg-white border border-slate-300 hover:border-slate-900 p-5 transition-colors relative">
              <ArrowUpRight size={18} className="absolute top-4 right-4 text-slate-400 group-hover:text-slate-900 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-[transform,color]" />
              <p className="font-extrabold text-lg tracking-tight uppercase pr-8" data-testid="book-card-name">{b.name}</p>
              <p className="text-xs text-slate-500 mt-1">
                {b.entry_count} entries{b.last_date ? ` · last ${ddmmyyyy(b.last_date)}` : ""}
              </p>
              <div className="mt-6 flex items-end justify-between gap-2">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">Available Bal</p>
                  <p className="font-mono text-2xl font-semibold tabular-nums tracking-tight" data-testid="book-card-available">{fmt(b.closing_available)}</p>
                </div>
                {b.mismatches > 0 ? (
                  <span className="text-xs font-semibold text-red-700 bg-red-50 border border-red-200 px-2 py-1 flex items-center gap-1">
                    <AlertTriangle size={12} /> {b.mismatches} Difference ≠ 0
                  </span>
                ) : (
                  <span className="text-xs font-semibold text-green-700 bg-green-50 border border-green-200 px-2 py-1 flex items-center gap-1">
                    <CheckCircle2 size={12} /> Balanced
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      </main>
      <BookDialog open={open} onOpenChange={setOpen} onSaved={(b) => nav(`/book/${b.id}`)} />
    </div>
  );
}
